import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useContactStore } from '../store/contactStore';
import { useBillboardStore } from '../store/billboardStore';
import { useProjectStore } from '../store/projectStore';
import { useWorkViewStore } from '../store/WorkViewStore';
import { useEditModeStore } from '../store/authStore';
import { useCareerListStore } from '../store/careerStore';
import { useMemberInfoStore } from '../store/memberInfoStore';
import { useTextViewStore } from '../store/TextViewStore';
import { useWorkIndexStore } from '../store/WorkIndexStore';
import { useTextIndexStore } from '../store/TextIndexStore';
import { resolveCurrentPage } from '../util/currentPage';
import type { PageType } from '../util/currentPage';
import { useGroupMemberStore } from '../store/groupMemberStore';
import { useProjectInfoListStore } from '../store/projectInfoStore';
import { useProjectElementListStore } from '../store/projectElementStore';

/**
 * 페이지별 미저장 변경 여부. 렌더 구독 없이 getState()로만 계산한다.
 * Cmd+E / 이동 전 가드가 동일한 소스를 사용한다.
 */
const PAGE_DIRTY_CHECKERS: Record<PageType, () => boolean> = {
  landing: () => false,
  onboard: () => false,
  billboard: () => useBillboardStore.getState().hasChanged,
  projects: () => false,
  works: () => {
    const workView = useWorkViewStore.getState();
    const workIndex = useWorkIndexStore.getState();
    return (
      workView.hasChanged ||
      workView.multiHasChanged ||
      workView.imageChanged ||
      workView.activeWorkDetailList.some((detail) => detail.hasChanged) ||
      (workIndex.isIndexingMode && workIndex.isChanged)
    );
  },
  texts: () => {
    const textView = useTextViewStore.getState();
    const textIndex = useTextIndexStore.getState();
    return (
      textView.hasChanged ||
      textView.multiHasChanged ||
      textView.activeTextBlockList.some((block) => block.hasChanged) ||
      (textIndex.isIndexingMode && textIndex.isChanged)
    );
  },
  members: () =>
    useGroupMemberStore
      .getState()
      .groupMemberList.some((member) => member.hasChanged),
  about: () => {
    const memberInfoChanged = useMemberInfoStore.getState().hasChanged;
    const careerChanged = useCareerListStore
      .getState()
      .careers.some((career) => career.hasChanged);
    return memberInfoChanged || careerChanged;
  },
  contact: () => useContactStore.getState().hasChanged,
  settings: () => false,
  projectDetail: () => {
    const projectChanged = useProjectStore.getState().hasChanged;
    const peChanged = useProjectElementListStore
      .getState()
      .projectElementList.some((pe) => pe.hasChanged);
    const piChanged = useProjectInfoListStore
      .getState()
      .projectInfoList.some((pi) => pi.hasChanged);
    return projectChanged || peChanged || piChanged;
  },
  policy: () => false,
  guide: () => false,
  poll: () => false,
  ADMIN: () => false,
  ERROR: () => false,
};

export const hasUnsavedChanges = (pathname: string): boolean => {
  if (!useEditModeStore.getState().isEditMode) return false;
  return PAGE_DIRTY_CHECKERS[resolveCurrentPage(pathname).page]();
};

export const useCheckBeforeMove = () => {
  const { pathname } = useLocation();
  const getCurrentPage = useCallback(
    () => resolveCurrentPage(pathname),
    [pathname],
  );

  /**
   * 이벤트가 발생한 시점의 store 값을 기준으로 변경사항을 확인한다.
   * @returns 변경사항이 있다: True, 변경사항이 없다: False
   */
  const checkPageHasChanges = useCallback(
    () => hasUnsavedChanges(pathname),
    [pathname],
  );

  return {
    getCurrentPage,
    checkPageHasChanges,
  };
};
